import { twMerge } from "tailwind-merge";
import {
  FormControl,
  FormHelperText,
  Menu as MUIMenu,
  MenuItem,
} from "@material-ui/core";
import React, { useMemo } from "react";

type MenuValue = string | number;

type MenuItem<T extends MenuValue> = Record<string, unknown> & {
  value: T;
};

type MenuProps<V extends MenuValue, T extends MenuItem<V>> = {
  name: string;
  defaultValue: V;
  selectedValue?: V;
  className?: string;
  classes?: Partial<{
    menu: string;
    error: string;
  }>;
  anchorOrigin?: {
    vertical: number | "bottom" | "center" | "top";
    horizontal: "left" | "center" | "right";
  };
  transformOrigin?: {
    vertical: number | "bottom" | "center" | "top";
    horizontal: "left" | "center" | "right";
  };
  items: Array<T>;
  renderItem: (item: T) => React.ReactElement;
  children: (item: T) => React.ReactElement;
  error?: string;
  setValue: (name: string, value: V) => void;
  disabled?: boolean;
};

const Menu = <V extends MenuValue, T extends MenuItem<V>>({
  name,
  disabled = false,
  defaultValue,
  selectedValue,
  className = "",
  classes = {},
  anchorOrigin,
  transformOrigin,
  items,
  renderItem,
  children,

  error,
  setValue,
}: MenuProps<V, T>): React.ReactElement => {
  const itemSelected = useMemo<T | undefined>(
    () =>
      selectedValue
        ? items.find((item) => item.value === selectedValue)
        : items.find((item) => item.value === defaultValue),
    [defaultValue, items, selectedValue]
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleClickButton = (event: React.MouseEvent<HTMLElement>): void => {
    !disabled && setAnchorEl(event.currentTarget);
  };

  const handleClickItem = (item: T) => () => {
    setValue(name, item.value);
    setAnchorEl(null);
  };

  return (
    <FormControl error={Boolean(error)}>
      <section>
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events*/}
        <span
          aria-haspopup="true"
          aria-controls={anchorEl ? "basic-menu" : undefined}
          aria-expanded={anchorEl ? "true" : undefined}
          onClick={handleClickButton}
          className={`cursor-pointer ${className}`}
        >
          {itemSelected
            ? children(itemSelected)
            : children({ value: defaultValue } as T)}
        </span>
        <MUIMenu
          id="basic-menu"
          open={Boolean(anchorEl)}
          anchorOrigin={anchorOrigin}
          transformOrigin={transformOrigin}
          className={classes?.menu}
          onClose={() => setAnchorEl(null)}
          anchorEl={anchorEl}
          MenuListProps={{
            "aria-labelledby": "basic-button",
          }}
        >
          {items.map((item) => (
            <MenuItem key={item.value} onClick={handleClickItem(item)}>
              {renderItem(item)}
            </MenuItem>
          ))}
        </MUIMenu>
        <FormHelperText
          className={twMerge(
            `text-red ml-0 h-4 truncate text-xs font-semibold ${
              error ? "block" : "hidden"
            }`,
            classes?.error
          )}
        >
          {error}
        </FormHelperText>
      </section>
    </FormControl>
  );
};

export type { MenuProps, MenuItem, MenuValue };
export { Menu };
